import { MetadataModules } from '../../enums/index.js';
import { addTypedProvider } from '../../metaData.js';
import { logging as cornerstoneLogging } from '@cornerstonejs/utils';
const cs3dLogger = cornerstoneLogging.metadataLog.getLogger('utilities.metadataProvider.compressedFrameData');
const PIXEL_DATA_KEYS = [
    'PixelData',
    'FramePixelData',
    'FloatPixelData',
    '7FE00010',
    '7fe00010',
    '7FE00008',
    '7fe00008',
];
const PARAMAP_PIXEL_DATA_KEYS = ['FloatPixelData', '7FE00008', '7fe00008'];
function getPixelDataFromNatural(natural) {
    for (const key of PIXEL_DATA_KEYS) {
        const val = natural[key];
        if (val !== undefined &&
            val !== null &&
            Array.isArray(val) &&
            val.length > 0) {
            return val;
        }
    }
    return undefined;
}
function getPixelDataKeyFromNatural(natural) {
    for (const key of PIXEL_DATA_KEYS) {
        const val = natural[key];
        if (val !== undefined &&
            val !== null &&
            Array.isArray(val) &&
            val.length > 0) {
            return key;
        }
    }
    return undefined;
}
function isParamapType(natural) {
    const key = getPixelDataKeyFromNatural(natural);
    return key !== undefined && PARAMAP_PIXEL_DATA_KEYS.includes(key);
}
function asView(buf) {
    if (buf instanceof ArrayBuffer) {
        return new Uint8Array(buf);
    }
    return buf;
}
function byteLength(buf) {
    if (buf instanceof ArrayBuffer) {
        return buf.byteLength;
    }
    return buf.byteLength;
}
function getFramePixelData(pixelDataTag, frameIndex) {
    const frame = pixelDataTag[frameIndex];
    if (frame === undefined || frame === null) {
        return undefined;
    }
    if (Array.isArray(frame)) {
        return frame.map(asView);
    }
    return asView(frame);
}
function getFramePixelDataFromSingleBuffer(pixelDataTag, frameIndex, numberOfFrames, natural) {
    if (pixelDataTag.length !== 1 || !isParamapType(natural)) {
        return undefined;
    }
    const buf = pixelDataTag[0];
    if (buf === undefined || buf === null) {
        return undefined;
    }
    const view = asView(buf);
    const totalLength = byteLength(buf);
    if (numberOfFrames <= 0 || totalLength % numberOfFrames !== 0) {
        return undefined;
    }
    const frameSize = totalLength / numberOfFrames;
    const offset = frameIndex * frameSize;
    if (offset + frameSize > totalLength) {
        return undefined;
    }
    cs3dLogger.warn('[compressedFrameData] Splitting single-buffer pixel data by numberOfFrames for paramap-type image; frameIndex=', frameIndex, ', numberOfFrames=', numberOfFrames, ', frameSize=', frameSize);
    const u8 = view instanceof Uint8Array
        ? view
        : new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
    return [u8.subarray(offset, offset + frameSize)];
}
function compressedFrameDataFromNatural(natural, frameIndex) {
    if (!natural) {
        return;
    }
    const pixelDataTag = getPixelDataFromNatural(natural);
    if (!pixelDataTag) {
        return;
    }
    const { TransferSyntaxUID: transferSyntaxUid } = natural;
    if (!transferSyntaxUid) {
        return;
    }
    const frameOfInterest = frameIndex ?? 0;
    const frameNumber = frameOfInterest + 1;
    const numberOfFrames = natural.NumberOfFrames != null ? Number(natural.NumberOfFrames) : 1;
    let pixelData;
    if (pixelDataTag.length === numberOfFrames) {
        pixelData = getFramePixelData(pixelDataTag, frameOfInterest);
    }
    else {
        pixelData = getFramePixelDataFromSingleBuffer(pixelDataTag, frameOfInterest, numberOfFrames, natural);
        if (pixelData === undefined) {
            pixelData = getFramePixelData(pixelDataTag, frameOfInterest);
        }
    }
    if (pixelData === undefined) {
        return;
    }
    return {
        transferSyntaxUid,
        frameOfInterest,
        frameNumber,
        pixelData,
    };
}
const COMPRESSED_FRAME_DATA_TYPE = MetadataModules.COMPRESSED_FRAME_DATA;
const compressedFrameDataProvider = (next, query, natural, options) => {
    const frameIndex = options?.frameIndex ?? 0;
    const value = compressedFrameDataFromNatural(natural, frameIndex);
    if (value) {
        return value;
    }
    return next(query, natural, options);
};
export function registerCompressedFrameDataProvider() {
    addTypedProvider(COMPRESSED_FRAME_DATA_TYPE, compressedFrameDataProvider);
}
